
import React from 'react';

interface ProcessStepProps {
  step: {
    number: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    details: string[];
  };
  index: number;
  stepProgress: number;
  isActive: boolean;
  isLastStep: boolean;
}

const ProcessStep = ({ step, index, stepProgress, isActive, isLastStep }: ProcessStepProps) => {
  return (
    <div
      className="relative mb-8 md:mb-10 last:mb-0"
      style={{
        opacity: stepProgress > 0 ? Math.min(stepProgress * 2, 1) : 1,
      }}
    >
      <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
        {/* Step Icon */}
        <div
          className={`flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full shadow-lg flex-shrink-0
            ${index % 3 === 0 ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white' : 
              index % 3 === 1 ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 
              'bg-gradient-to-r from-purple-500 to-pink-500 text-white'}`}
        >
          {step.icon}
        </div>

        {/* Step Content */}
        <div className="flex-1 space-y-3 md:space-y-4">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full text-white font-bold text-base md:text-lg shadow-md
              ${index % 3 === 0 ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 
                index % 3 === 1 ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 
                'bg-gradient-to-r from-purple-500 to-pink-500'}`}
          >
            {step.number}
          </div>

          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 max-w-2xl">
              {step.title}
            </h3>
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed max-w-2xl">
              {step.description}
            </p>
            <ul className="space-y-1 md:space-y-1.5 max-w-2xl">
              {step.details.map((detail, detailIndex) => (
                <li 
                  key={detailIndex}
                  className="flex items-center text-xs md:text-sm text-gray-500"
                >
                  <div className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full mr-2 md:mr-2.5
                    ${index % 3 === 0 ? 'bg-emerald-400' : 
                      index % 3 === 1 ? 'bg-blue-400' : 
                      'bg-purple-400'}`}></div>
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Connection Line */}
      {!isLastStep && (
        <div
          className={`absolute top-12 md:top-16 left-6 md:left-8 w-px h-8 md:h-10 transform origin-top
            ${index % 3 === 0 ? 'bg-gradient-to-b from-emerald-400 to-cyan-400' : 
              index % 3 === 1 ? 'bg-gradient-to-b from-blue-400 to-purple-400' : 
              'bg-gradient-to-b from-purple-400 to-pink-400'}`}
          style={{
            scaleY: Math.min(stepProgress * 1.5, 1),
            opacity: Math.min(stepProgress * 1.5, 1)
          }}
        />
      )}
    </div>
  );
};

export default ProcessStep;
